import React, { useState, useEffect } from 'react';
import './DashboardAnimations.css'; // Make sure this CSS file exists or remove if not needed
import {
    Shield,
    ShieldCheck,
    ShieldAlert,
    UserCheck,
    Lock,
    Unlock,
    AlertTriangle,
    AlertCircle,
    CheckCircle,
    Clock,
    RefreshCw,
    Eye,
    Terminal,
    UserX,
    FolderLock,
    Network,
    Server,
    // Use a different alias for the second Shield import if needed, or just use one.
    // Using ShieldIcon for clarity if you intended two distinct uses, otherwise remove one import.
    Shield as ShieldIcon,
    ArrowUp,
    ArrowDown
} from 'lucide-react';

// --- MAPPINGS ---
const colorToBgClass = {
    emerald: 'bg-emerald-500',
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    yellow: 'bg-yellow-500', // Added for chart
};

const colorToBgClassLight = {
    emerald: 'bg-emerald-500/10',
    red: 'bg-red-500/10',
    blue: 'bg-blue-500/10',
    amber: 'bg-amber-500/10',
    yellow: 'bg-yellow-500/10',
};

const colorToTextClass = {
    emerald: 'text-emerald-500',
    red: 'text-red-500',
    blue: 'text-blue-500',
    amber: 'text-amber-500',
    yellow: 'text-yellow-500',
};

const colorToTextClassLight = {
    emerald: 'text-emerald-400',
    red: 'text-red-400',
    blue: 'text-blue-400',
    amber: 'text-amber-400',
    yellow: 'text-yellow-400',
};

const colorToGradientAccentClass = {
    emerald: 'bg-emerald-500/5',
    red: 'bg-red-500/5',
    blue: 'bg-blue-500/5',
    amber: 'bg-amber-500/5',
    yellow: 'bg-yellow-500/5',
};

const severityToBadgeClass = {
    critical: 'bg-red-500/20 text-red-400',
    high: 'bg-yellow-500/20 text-yellow-400',
    medium: 'bg-amber-500/20 text-amber-400',
    low: 'bg-blue-500/20 text-blue-400',
};

const activityTypeToBgClass = {
    info: 'bg-blue-500/10',
    warning: 'bg-amber-500/10',
    error: 'bg-red-500/10',
    success: 'bg-emerald-500/10', // Assuming success maps to emerald
};

// --- COMPONENT ---
const SecurityDashboard = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 1500);
        return () => clearTimeout(timer);
    }, []);

    const handleRefresh = () => {
        setRefreshing(true);
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
            setRefreshing(false);
        }, 1000);
    };

    const SecurityParticle = ({ delay, color, top, left }) => (
        <div
            // Note: Dynamic classes here might also need safelisting if colors vary widely
            // or use the mapping approach if colors are fixed like 'emerald' or 'blue'
            className={`absolute h-1.5 w-1.5 rounded-full ${color === 'emerald' ? 'bg-emerald-500' : 'bg-blue-500'} animate-security-scan`}
            style={{
                top: `${top}%`,
                left: `${left}%`,
                animationDelay: `${delay}s`,
                animationDuration: '3s'
            }}
        />
    );

    return (
        <div className="flex justify-center items-center p-4 bg-slate-950 min-h-screen">
            <div className="w-full max-w-6xl mx-auto">
                <div className="relative bg-slate-900 rounded-xl shadow-2xl border border-slate-800 overflow-hidden">
                    {/* Header */}
                    <div className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700">
                        {/* ... header content ... */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-blue-500 to-emerald-500"></div>

                        {/* Security grid pattern decorative elements */}
                        <div className="absolute inset-0 grid grid-cols-12 grid-rows-6 gap-px opacity-10">
                            {[...Array(72)].map((_, i) => (
                                <div key={i} className="bg-white h-full w-full"></div>
                            ))}
                        </div>

                        {/* Security scan animation */}
                        <div className="absolute inset-0 overflow-hidden">
                            {[...Array(20)].map((_, i) => (
                                <SecurityParticle
                                    key={i}
                                    delay={i * 0.2}
                                    // Pass specific color names for the mapping in SecurityParticle
                                    color={i % 3 === 0 ? 'emerald' : 'blue'}
                                    top={10 + Math.random() * 80}
                                    left={Math.random() * 100}
                                />
                            ))}
                        </div>

                        <div className="flex items-center justify-between px-6 py-4 relative z-10">
                            {/* ... header inner content (Shield, title, refresh button etc) ... */}
                            <div className="flex items-center space-x-4">
                                <div className="bg-gradient-to-r from-emerald-500 to-blue-500 p-2 rounded-lg">
                                    <Shield className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-white">Security Dashboard</h1>
                                    <p className="text-slate-400 text-sm">Threat detection & protection status</p>
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                <button onClick={handleRefresh} className="flex items-center space-x-1 text-slate-300 hover:text-white">
                                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                                    <span className="text-sm">Refresh</span>
                                </button>

                                <div className="flex items-center space-x-1 px-3 py-1 bg-green-500/10 rounded-full border border-green-500/20">
                                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                    <span className="text-green-400 text-xs font-medium">Protected</span>
                                </div>

                                <div className="hidden md:flex items-center space-x-1 text-slate-400">
                                    <Clock className="w-4 h-4" />
                                    <span className="text-sm">Last scan: <span className="text-slate-300">12 minutes ago</span></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex items-center px-6 py-2 bg-slate-800/50 border-b border-slate-700/50 overflow-x-auto">
                        {/* ... tabs content ... */}
                        <div className="flex space-x-1">
                            {['overview', 'threats', 'compliance', 'access'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-2 rounded-t-lg text-sm font-medium capitalize transition-colors ${activeTab === tab
                                        ? 'bg-slate-800 text-white border-t border-l border-r border-slate-600/50'
                                        : 'text-slate-400 hover:text-slate-300'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        <div className="ml-auto flex items-center space-x-2">
                            <button className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700">
                                <Eye className="w-4 h-4" />
                            </button>
                            <button className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700">
                                <Terminal className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="p-6">
                        {isLoading ? (
                            <div className="flex items-center justify-center h-96">
                                {/* ... loading indicator ... */}
                                <div className="relative">
                                    <Shield className="w-12 h-12 text-slate-600 animate-pulse" />
                                    <div className="absolute w-full h-full top-0 left-0 flex items-center justify-center">
                                        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Top metrics cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                    {[
                                        // ... metric data (make sure 'color' key matches keys in mapping objects)
                                        {
                                            title: 'Security Score',
                                            value: '89/100',
                                            trend: 'up',
                                            change: '3 pts',
                                            icon: <ShieldCheck className="w-5 h-5" />,
                                            color: 'emerald' // Key for mapping
                                        },
                                        {
                                            title: 'Active Threats',
                                            value: '2',
                                            trend: 'down',
                                            change: '5',
                                            icon: <ShieldAlert className="w-5 h-5" />,
                                            color: 'red' // Key for mapping
                                        },
                                        {
                                            title: 'Auth Attempts',
                                            value: '1,432',
                                            trend: 'up',
                                            change: '127',
                                            icon: <UserCheck className="w-5 h-5" />,
                                            color: 'blue' // Key for mapping
                                        },
                                        {
                                            title: 'Endpoints Protected',
                                            value: '47/48',
                                            trend: 'up',
                                            change: '2',
                                            icon: <Network className="w-5 h-5" />,
                                            color: 'amber' // Key for mapping
                                        }
                                    ].map((metric, index) => (
                                        <div
                                            key={index}
                                            className="relative bg-slate-800 rounded-xl p-4 border border-slate-700 overflow-hidden"
                                        >
                                            {/* Use mapped class */}
                                            <div className={`absolute top-0 left-0 w-full h-1 ${colorToBgClass[metric.color]}`}></div>

                                            <div className="relative z-10">
                                                <div className="flex items-center justify-between mb-2">
                                                    {/* Use mapped classes */}
                                                    <div className={`p-2 rounded-lg ${colorToBgClassLight[metric.color]} ${colorToTextClass[metric.color]}`}>
                                                        {metric.icon}
                                                    </div>
                                                    {/* Conditional trend color - Use mapped classes */}
                                                    <div className={`flex items-center ${metric.trend === 'up'
                                                            ? (metric.title === 'Active Threats' ? colorToTextClassLight['red'] : colorToTextClassLight['emerald'])
                                                            : (metric.title === 'Active Threats' ? colorToTextClassLight['emerald'] : colorToTextClassLight['red'])
                                                        } text-sm`}>
                                                        {metric.trend === 'up' ? <ArrowUp className="w-3 h-3 mr-1" /> : <ArrowDown className="w-3 h-3 mr-1" />}
                                                        {metric.change}
                                                    </div>
                                                </div>

                                                <div className="mb-1 text-slate-400 text-sm">{metric.title}</div>
                                                <div className="text-2xl font-bold text-white">{metric.value}</div>
                                            </div>

                                            {/* Decorative gradient - Use mapped class */}
                                            <div className={`absolute bottom-0 right-0 w-24 h-24 ${colorToGradientAccentClass[metric.color]} rounded-full blur-2xl -mb-8 -mr-8`}></div>
                                        </div>
                                    ))}
                                </div>

                                {/* Main charts grid */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                                    {/* Large chart - Security Events */}
                                    <div className="lg:col-span-2 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        {/* ... chart header ... */}
                                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
                                            <div>
                                                <h3 className="font-medium text-white">Security Events</h3>
                                                <p className="text-slate-400 text-sm">Detected incidents over time</p>
                                            </div>
                                            <div className="flex space-x-2">
                                                <button className="px-3 py-1 text-xs text-slate-300 bg-slate-700 rounded-full hover:bg-slate-600">Day</button>
                                                <button className="px-3 py-1 text-xs text-white bg-emerald-500 rounded-full">Week</button>
                                                <button className="px-3 py-1 text-xs text-slate-300 bg-slate-700 rounded-full hover:bg-slate-600">Month</button>
                                            </div>
                                        </div>

                                        <div className="px-4 py-4">
                                            <div className="h-72 w-full relative overflow-hidden">
                                                <div className="absolute bottom-0 left-0 right-0 h-64 flex items-end">
                                                    {/* ... axes and labels ... */}
                                                    {/* X-axis */}
                                                    <div className="absolute bottom-0 left-0 right-0 h-px bg-slate-700"></div>

                                                    {/* Y-axis */}
                                                    <div className="absolute top-0 bottom-0 left-0 w-px bg-slate-700"></div>

                                                    {/* Y-axis labels */}
                                                    {[0, 5, 10, 15, 20].map((value, i) => (
                                                        // Adjusted positioning slightly for better alignment
                                                        <div key={i} className="absolute left-0 text-xs text-slate-500 transform -translate-y-1/2" style={{ bottom: `${(value / 20) * 100}%` }}>
                                                            <span className="block w-6 text-right -ml-7">{value}</span>
                                                            {/* Render grid line only if value > 0 */}
                                                            {value > 0 && <div className="absolute left-0 right-0 h-px bg-slate-700/30 ml-1"></div>}
                                                        </div>
                                                    ))}


                                                    {/* --- FIX HERE: Simulated bar chart --- */}
                                                    <div className="relative flex-1 flex items-end justify-around pl-8 pr-4 h-full">
                                                        {[
                                                            { day: 'Mon', value: 5, color: 'blue' },
                                                            { day: 'Tue', value: 8, color: 'blue' },
                                                            { day: 'Wed', value: 12, color: 'yellow' },
                                                            { day: 'Thu', value: 18, color: 'red' },
                                                            { day: 'Fri', value: 7, color: 'blue' },
                                                            { day: 'Sat', value: 3, color: 'emerald' },
                                                            { day: 'Sun', value: 4, color: 'emerald' }
                                                        ].map((item, i) => (
                                                            <div key={i} className="flex flex-col items-center h-full justify-end group">
                                                                <div
                                                                    // Use the mapped class for background color
                                                                    className={`w-10 md:w-12 ${colorToBgClass[item.color]}/80 rounded-t-sm animate-grow-up transition-all duration-300 group-hover:opacity-100 opacity-80`}
                                                                    style={{
                                                                        // Calculate height relative to the container (h-64 = 16rem = 256px)
                                                                        // Max height corresponds to max value (e.g., 20)
                                                                        height: `${(item.value / 20) * 100}%`, // Use percentage of parent height
                                                                        maxHeight: '100%', // Ensure it doesn't exceed container
                                                                        animationDelay: `${i * 0.1}s`
                                                                    }}
                                                                ></div>
                                                                <div className="text-xs text-slate-500 mt-2">{item.day}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Legend - Use mapped classes */}
                                                <div className="absolute top-4 right-4 flex flex-col space-y-2">
                                                    <div className="flex items-center text-xs">
                                                        <div className={`w-3 h-3 rounded-sm ${colorToBgClass['emerald']} mr-2`}></div>
                                                        <span className="text-slate-300">Low severity</span>
                                                    </div>
                                                    <div className="flex items-center text-xs">
                                                        <div className={`w-3 h-3 rounded-sm ${colorToBgClass['blue']} mr-2`}></div>
                                                        <span className="text-slate-300">Medium severity</span>
                                                    </div>
                                                    <div className="flex items-center text-xs">
                                                        <div className={`w-3 h-3 rounded-sm ${colorToBgClass['yellow']} mr-2`}></div>
                                                        <span className="text-slate-300">High severity</span>
                                                    </div>
                                                    <div className="flex items-center text-xs">
                                                        <div className={`w-3 h-3 rounded-sm ${colorToBgClass['red']} mr-2`}></div>
                                                        <span className="text-slate-300">Critical severity</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Access Overview */}
                                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        {/* ... Access Overview content (circular chart seems fine) ... */}
                                        <div className="px-4 py-3 border-b border-slate-700">
                                            <h3 className="font-medium text-white">Access Overview</h3>
                                            <p className="text-slate-400 text-sm">Current authorization status</p>
                                        </div>

                                        <div className="px-4 py-4">
                                            {/* Circular access chart */}
                                            <div className="h-52 w-full flex items-center justify-center">
                                                <div className="relative w-40 h-40">
                                                    {/* SVG Shield Chart */}
                                                    <svg viewBox="0 0 100 100" className="w-full h-full">
                                                        {/* Background Circle */}
                                                        <circle cx="50" cy="50" r="45" fill="transparent" stroke="#334155" strokeWidth="8" /> {/* slate-700 */}
                                                        {/* Foreground Circle - Emerald */}
                                                        <circle
                                                            cx="50" cy="50" r="45"
                                                            fill="transparent"
                                                            stroke="#10B981" // emerald-500
                                                            strokeWidth="8"
                                                            strokeDasharray="282.7" // Circumference (2 * PI * 45)
                                                            strokeDashoffset="42.4" /* 282.7 * (1 - 0.85) for 85% */
                                                            transform="rotate(-90 50 50)" // Start from top
                                                            className="animate-chart-fill"
                                                            style={{ animationDelay: '0.2s' }}
                                                        />
                                                        {/* Optional: Inner shield icon or shape if desired */}
                                                        {/* <path ... /> */}
                                                    </svg>
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <div className="text-center">
                                                            <div className="text-3xl font-bold text-emerald-400">85%</div>
                                                            <div className="text-xs text-slate-400">Secure Access</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Access stats */}
                                            <div className="grid grid-cols-2 gap-4 mt-4">
                                                {/* Use mapped classes */}
                                                <div className="bg-slate-700/30 rounded-lg p-3">
                                                    <div className="flex items-center justify-between">
                                                        <Lock className={`w-5 h-5 ${colorToTextClass['emerald']}`} />
                                                        <span className={`${colorToTextClassLight['emerald']} text-xs`}>Secured</span>
                                                    </div>
                                                    <div className="mt-2 text-xl font-semibold text-white">41</div>
                                                    <div className="text-xs text-slate-400">Access points</div>
                                                </div>

                                                <div className="bg-slate-700/30 rounded-lg p-3">
                                                    <div className="flex items-center justify-between">
                                                        <Unlock className={`w-5 h-5 ${colorToTextClass['amber']}`} />
                                                        <span className={`${colorToTextClassLight['amber']} text-xs`}>Review needed</span>
                                                    </div>
                                                    <div className="mt-2 text-xl font-semibold text-white">7</div>
                                                    <div className="text-xs text-slate-400">Access points</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom row - three equal cards */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Threat Detection */}
                                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        {/* ... Threat header ... */}
                                        <div className="px-4 py-3 border-b border-slate-700">
                                            <h3 className="font-medium text-white">Active Threats</h3>
                                            <p className="text-slate-400 text-sm">Detected security issues</p>
                                        </div>
                                        <div className="px-4 py-4">
                                            <div className="space-y-4 max-h-[240px] overflow-y-auto">
                                                {[
                                                    // ... threat data (ensure 'color' and 'severity' keys match mappings)
                                                    {
                                                        name: 'Suspicious Login Attempt',
                                                        severity: 'critical',
                                                        time: '12m ago',
                                                        icon: <UserX className="w-4 h-4" />,
                                                        color: 'red'
                                                    },
                                                    {
                                                        name: 'Outdated Firmware (Server 03)',
                                                        severity: 'medium',
                                                        time: '1h ago',
                                                        icon: <Server className="w-4 h-4" />,
                                                        color: 'amber'
                                                    },
                                                    {
                                                        name: 'Unencrypted Data Transfer',
                                                        severity: 'low',
                                                        time: '3h ago',
                                                        icon: <FolderLock className="w-4 h-4" />,
                                                        color: 'blue'
                                                    },
                                                    {
                                                        name: 'Suspicious Port Scan',
                                                        severity: 'medium',
                                                        time: '5h ago',
                                                        icon: <Network className="w-4 h-4" />,
                                                        color: 'amber'
                                                    }
                                                ].map((threat, i) => (
                                                    <div key={i} className="relative flex items-start p-3 rounded-lg hover:bg-slate-700/30 transition-colors">
                                                        {/* Use mapped classes */}
                                                        <div className={`p-2 rounded-lg ${colorToBgClassLight[threat.color]} ${colorToTextClass[threat.color]} mr-3`}>
                                                            {threat.icon}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex justify-between">
                                                                <p className="text-sm font-medium text-slate-300 truncate pr-1">{threat.name}</p>
                                                                {/* Use mapped class */}
                                                                <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ml-2 ${severityToBadgeClass[threat.severity]}`}>
                                                                    {threat.severity}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-slate-500 mt-1">{threat.time}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Recent Activity */}
                                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        {/* ... Activity header ... */}
                                        <div className="px-4 py-3 border-b border-slate-700">
                                            <h3 className="font-medium text-white">Recent Activity</h3>
                                            <p className="text-slate-400 text-sm">Latest security events</p>
                                        </div>
                                        <div className="px-4 py-4">
                                            <div className="space-y-3 max-h-[240px] overflow-y-auto">
                                                {[
                                                    // ... activity data (ensure 'type' key matches mapping)
                                                    {
                                                        type: 'success', // Changed to 'success' to match mapping
                                                        message: 'Scheduled security scan completed',
                                                        time: '4m ago',
                                                        icon: <CheckCircle className="w-4 h-4 text-emerald-500" />
                                                    },
                                                    {
                                                        type: 'warning',
                                                        message: 'New user "jsmith" created with admin privileges',
                                                        time: '27m ago',
                                                        icon: <AlertCircle className="w-4 h-4 text-amber-500" />
                                                    },
                                                    {
                                                        type: 'error',
                                                        message: 'Failed login attempt (198.51.100.34)',
                                                        time: '1h ago',
                                                        icon: <AlertTriangle className="w-4 h-4 text-red-500" />
                                                    },
                                                    {
                                                        type: 'info',
                                                        message: 'Firewall rules updated by admin',
                                                        time: '2h ago',
                                                        icon: <ShieldIcon className="w-4 h-4 text-blue-500" />
                                                    },
                                                    {
                                                        type: 'info',
                                                        message: 'System updates installed on 12 servers',
                                                        time: '5h ago',
                                                        icon: <Server className="w-4 h-4 text-blue-500" />
                                                    }
                                                ].map((activity, i) => (
                                                    <div key={i} className="flex items-start p-2 rounded-lg hover:bg-slate-700/30">
                                                        {/* Use mapped class */}
                                                        <div className={`flex-shrink-0 p-2 rounded-full ${activityTypeToBgClass[activity.type]} mr-3`}>
                                                            {activity.icon} {/* Icon already has color */}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-slate-300">{activity.message}</p>
                                                            <p className="text-xs text-slate-500">{activity.time}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Security Compliance */}
                                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        {/* ... Compliance header ... */}
                                        <div className="px-4 py-3 border-b border-slate-700">
                                            <h3 className="font-medium text-white">Compliance Status</h3>
                                            <p className="text-slate-400 text-sm">Regulatory requirements</p>
                                        </div>
                                        <div className="px-4 py-4">
                                            <div className="space-y-3 max-h-[240px] overflow-y-auto">
                                                {[
                                                    // ... compliance data (ensure 'color' key matches mapping)
                                                    { name: 'GDPR', progress: 92, color: 'emerald' },
                                                    { name: 'ISO 27001', progress: 87, color: 'emerald' },
                                                    { name: 'HIPAA', progress: 78, color: 'amber' },
                                                    { name: 'SOC 2', progress: 95, color: 'emerald' },
                                                    { name: 'PCI DSS', progress: 68, color: 'red' }
                                                ].map((item, i) => (
                                                    <div key={i} className="relative">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <div className="text-sm font-medium text-slate-300">{item.name}</div>
                                                            <div className="text-sm font-medium text-slate-300">{item.progress}%</div>
                                                        </div>
                                                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                                            {/* Use mapped class */}
                                                            <div
                                                                className={`h-full ${colorToBgClass[item.color]} rounded-full transition-all duration-1000 ease-out`}
                                                                style={{ width: `${item.progress}%` }}
                                                            ></div>
                                                        </div>
                                                        <div className="flex justify-between mt-1">
                                                            {/* Use mapped class for text color */}
                                                            <span className={`text-xs ${item.progress < 75 ? colorToTextClassLight['red'] :
                                                                    item.progress < 90 ? colorToTextClassLight['amber'] :
                                                                        colorToTextClassLight['emerald']
                                                                }`}>{
                                                                    item.progress < 75 ? 'Action needed' :
                                                                        item.progress < 90 ? 'In progress' : 'Compliant'
                                                                }</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between px-6 py-4 bg-slate-800/50 border-t border-slate-700/50">
                        {/* ... footer content ... */}
                        <div className="flex items-center space-x-4">
                            <button className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center space-x-2 transition-colors">
                                <AlertTriangle className="w-4 h-4" />
                                <span>View All Alerts</span>
                            </button>
                            <button className="px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg flex items-center space-x-2 transition-colors">
                                <Terminal className="w-4 h-4" />
                                <span>Security Logs</span>
                            </button>
                        </div>

                        <div className="flex items-center space-x-3">
                            <button onClick={handleRefresh} className="text-slate-400 hover:text-white transition-colors">
                                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                            </button>
                            <button className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg flex items-center space-x-2 transition-colors">
                                <ShieldCheck className="w-4 h-4" />
                                <span>Run Security Scan</span>
                            </button>
                        </div>
                    </div>

                    {/* Hidden Modals/Panels/Toast Placeholders */}
                    {/* ... */}

                </div> {/* End of relative bg-slate-900 */}
            </div> {/* End of max-w-6xl */}
        </div> // End of flex justify-center
    );
};

export default SecurityDashboard;

